import { useState, useCallback } from 'react';
import { projectsApi, messagesApi, usersApi } from '../lib/api';
import { toast } from 'sonner';

export function useDashboardData() {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [projectsRes, usersRes, conversationsRes, unreadRes] = await Promise.all([
        projectsApi.getAll(),
        usersApi.getAll(),
        messagesApi.getConversations(),
        messagesApi.getUnreadCount()
      ]);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
      setConversations(conversationsRes.data);
      setUnreadCount(unreadRes.data.count);
    } catch (error) {
      toast.error('Failed to load data');
      void error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { projects, users, conversations, unreadCount, loading, fetchData };
}

export function useCreateProject(fetchData) {
  return useCallback(async (projectData) => {
    try {
      await projectsApi.create(projectData);
      toast.success('Project created successfully');
      fetchData();
      return true;
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to create project';
      toast.error(typeof msg === 'string' ? msg : 'Failed to create project');
      return false;
    }
  }, [fetchData]);
}

export function useUpdateProject(fetchData) {
  return useCallback(async (projectId, updateData) => {
    try {
      await projectsApi.update(projectId, updateData);
      toast.success('Project updated successfully');
      fetchData();
      return true;
    } catch (error) {
      toast.error('Failed to update project');
      void error;
      return false;
    }
  }, [fetchData]);
}

export function useDeleteProject(fetchData) {
  return useCallback(async (projectId, { skipConfirm = false } = {}) => {
    if (!skipConfirm && !window.confirm('Are you sure you want to delete this project?')) return false;
    try {
      await projectsApi.delete(projectId);
      toast.success('Project deleted successfully');
      fetchData();
      return true;
    } catch (error) {
      toast.error('Failed to delete project');
      void error;
      return false;
    }
  }, [fetchData]);
}

export function useMoveStage(fetchData) {
  return useCallback(async (project, direction) => {
    const stages = project.stages || [];
    const currentIndex = stages.findIndex(s => s.id === project.current_stage_id);
    const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (newIndex < 0 || newIndex >= stages.length) return;
    try {
      await projectsApi.update(project.id, { current_stage_id: stages[newIndex].id });
      toast.success(`Moved to ${stages[newIndex].name}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update stage');
      void error;
    }
  }, [fetchData]);
}

export function useMessages(fetchData) {
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  const handleOpenChat = useCallback(async (userId) => {
    setSelectedChat(userId);
    try {
      const res = await messagesApi.getMessages(userId);
      setMessages(res.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to load messages');
      void error;
    }
  }, [fetchData]);

  const handleSendMessage = useCallback(async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat) return;
    try {
      await messagesApi.send({ recipient_id: selectedChat, content: newMessage });
      setNewMessage('');
      const res = await messagesApi.getMessages(selectedChat);
      setMessages(res.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to send message');
      void error;
    }
  }, [fetchData, newMessage, selectedChat]);

  return { selectedChat, messages, newMessage, setNewMessage, handleOpenChat, handleSendMessage };
}
